import React from 'react'
import { Box, VStack, Text, Spinner, Link } from '@chakra-ui/react'
import { FormProvider, useFormContext } from './context/FormContext'
import { DonationFormUiProvider } from './context/UiContext'
import { StepNavigator } from './StepNavigator'
import { AmountStep } from './steps/AmountStep'
import { PaymentStep } from './steps/PaymentStep'
import { ReceiptStep } from './steps/ReceiptStep'
import type { DonationFormPaymentOverrides } from './types'

export interface UpdateBillingDonationFormProps {
  receiptNumber: string
  utmCampaign: string | null
  paymentOverrides?: DonationFormPaymentOverrides
  initialStep?: 'amount' | 'payment'
  /** Provide updateToken for public billing update flow */
  updateToken?: string
  /** Set to true for admin flow using authenticated session (omit updateToken) */
  withAuth?: boolean
  onAuthNetModalChange?: (isOpen: boolean) => void
}

const UpdateBillingDonationFormContent: React.FC = () => {
  const { state } = useFormContext()
  const { currentStep, formDetails, isLoading, error } = state

  if (isLoading) {
    return (
      <VStack gap={4} py={8}>
        <Spinner size="xl" color="teal.solid" />
        <Text color="fg.muted">Loading donation details...</Text>
      </VStack>
    )
  }

  if (error) {
    return (
      <VStack gap={3} py={8} textAlign="center">
        <Text fontWeight="semibold" color="red.fg">
          Unable to Load Donation
        </Text>
        {/* <Text color="fg.muted">{error}</Text> */}
        <Link href="/donate/" color="teal.solid">
          Make a new donation
        </Link>
      </VStack>
    )
  }

  if (!formDetails) {
    return null
  }

  return (
    <VStack align="stretch" overflow="visible">
      {currentStep !== 'receipt' && <StepNavigator />}

      <Box overflow="visible">
        {currentStep === 'amount' && <AmountStep />}
        {currentStep === 'payment' && <PaymentStep />}
        {currentStep === 'receipt' && <ReceiptStep />}
      </Box>
    </VStack>
  )
}

export const UpdateBillingDonationForm: React.FC<UpdateBillingDonationFormProps> = ({ 
  receiptNumber, 
  utmCampaign, 
  paymentOverrides, 
  initialStep,
  updateToken,
  withAuth,
  onAuthNetModalChange,
}) => {
  return (
    <Box containerType="inline-size" w="full" overflow="visible">
      <Box
        w="full"
        minW="280px"
        maxW="md"
        mx="auto"
        pt={{ base: 1, _cqMd: 2 }}
        px={{ base: 3, _cqMd: 6 }}
        pb={{ base: 3, _cqMd: 6 }}
        overflow="visible"
      >
        <DonationFormUiProvider showStepNavigator={true} onAuthNetModalChange={onAuthNetModalChange}>
          <FormProvider
            mode="billing-update"
            receiptNumber={receiptNumber}
            updateToken={updateToken}
            withAuth={withAuth}
            utmCampaign={utmCampaign}
            paymentOverrides={paymentOverrides}
            initialStep={initialStep}
          >
            <UpdateBillingDonationFormContent />
          </FormProvider>
        </DonationFormUiProvider>
      </Box>
    </Box>
  )
}


